import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function AdminProjectSettingsPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: '', slug: '' });
  const [newCapability, setNewCapability] = useState({ name: '', slug: '' });

  useEffect(() => {
    const isAuth = sessionStorage.getItem('admin_auth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const [categoriesRes, capabilitiesRes] = await Promise.all([
        fetch(`${backendUrl}/api/admin/project-categories`),
        fetch(`${backendUrl}/api/admin/capabilities`)
      ]);
      setCategories(await categoriesRes.json());
      setCapabilities(await capabilitiesRes.json());
    } catch (err) {
      toast.error('Failed to load data');
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  };

  const addCategory = async () => {
    if (!newCategory.name) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const categoryData = {
        ...newCategory,
        slug: newCategory.slug || generateSlug(newCategory.name),
        order: categories.length + 1
      };

      await fetch(`${backendUrl}/api/admin/project-categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryData)
      });

      toast.success('Category added');
      setNewCategory({ name: '', slug: '' });
      loadData();
    } catch (err) {
      toast.error('Failed to add category');
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category? Projects using it will need to be updated.')) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/admin/project-categories/${id}`, { method: 'DELETE' });
      toast.success('Category deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete category');
    }
  };

  const addCapability = async () => {
    if (!newCapability.name) {
      toast.error('Please enter a capability name');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const capabilityData = {
        ...newCapability,
        slug: newCapability.slug || generateSlug(newCapability.name),
        order: capabilities.length + 1
      };

      await fetch(`${backendUrl}/api/admin/capabilities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(capabilityData)
      });

      toast.success('Capability added');
      setNewCapability({ name: '', slug: '' });
      loadData();
    } catch (err) {
      toast.error('Failed to add capability');
    }
  };

  const deleteCapability = async (id) => {
    if (!window.confirm('Delete this capability?')) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/admin/capabilities/${id}`, { method: 'DELETE' });
      toast.success('Capability deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete capability');
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--bk-bg))] p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin/projects">
            <Button variant="ghost"><ArrowLeft size={16} /></Button>
          </Link>
          <div>
            <h1 className="font-display text-3xl text-[hsl(var(--bk-fg))]">Project Settings</h1>
            <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] mt-1">Manage categories and capabilities</p>
          </div>
        </div>

        <Tabs defaultValue="categories" className="space-y-6">
          <TabsList className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))]">
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="capabilities">Capabilities</TabsTrigger>
          </TabsList>

          {/* CATEGORIES TAB */}
          <TabsContent value="categories">
            <div className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-6">
              <h2 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-6">Project Categories</h2>
              
              <div className="space-y-4 mb-6">
                {categories.map(cat => (
                  <div key={cat.id} className="flex items-center justify-between p-4 bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))] rounded">
                    <div>
                      <p className="font-body text-[hsl(var(--bk-fg))]">{cat.name}</p>
                      <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-muted))]">{cat.slug}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => deleteCategory(cat.id)}>
                      <Trash2 size={14} className="text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="border-t border-[hsl(var(--bk-line))] pt-6">
                <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-4">Add New Category</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Name</Label>
                    <Input
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value, slug: generateSlug(e.target.value) })}
                      placeholder="Music Video"
                      className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                    />
                  </div>
                  <div>
                    <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Slug (auto-generated)</Label>
                    <Input
                      value={newCategory.slug}
                      onChange={(e) => setNewCategory({ ...newCategory, slug: e.target.value })}
                      placeholder="music-video"
                      className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                    />
                  </div>
                </div>
                <Button onClick={addCategory} className="mt-4 bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))]">
                  <Plus size={16} className="mr-2" /> Add Category
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* CAPABILITIES TAB */}
          <TabsContent value="capabilities">
            <div className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-6">
              <h2 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-6">Capabilities</h2>
              
              <div className="space-y-4 mb-6">
                {capabilities.map(cap => (
                  <div key={cap.id} className="flex items-center justify-between p-4 bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))] rounded">
                    <div>
                      <p className="font-body text-[hsl(var(--bk-fg))]">{cap.name}</p>
                      <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-muted))]">{cap.slug}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => deleteCapability(cap.id)}>
                      <Trash2 size={14} className="text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="border-t border-[hsl(var(--bk-line))] pt-6">
                <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-4">Add New Capability</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Name</Label>
                    <Input
                      value={newCapability.name}
                      onChange={(e) => setNewCapability({ ...newCapability, name: e.target.value, slug: generateSlug(e.target.value) })}
                      placeholder="Sound Design"
                      className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                    />
                  </div>
                  <div>
                    <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">Slug (auto-generated)</Label>
                    <Input
                      value={newCapability.slug}
                      onChange={(e) => setNewCapability({ ...newCapability, slug: e.target.value })}
                      placeholder="sound-design"
                      className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                    />
                  </div>
                </div>
                <Button onClick={addCapability} className="mt-4 bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))]">
                  <Plus size={16} className="mr-2" /> Add Capability
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
